import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ReceiptLong as ReceiptIcon,
  AttachMoney as MoneyIcon,
  TrendingDown as ExpenseIcon,
  AccountBalanceWallet as WalletIcon,
  Calculate as TaxIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
import { format } from 'date-fns';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';

const EXPENSE_CATEGORIES = [
  'Fuel',
  'Vehicle Insurance',
  'Vehicle Maintenance',
  'Franchise Fees',
  'Dual Controls Rental',
  'Marketing & Advertising',
  'Office & Admin Expenses',
  'Other Business Costs'
];

export const ExpenseTracker: React.FC = () => {
  const confirm = useConfirm();
  const showToast = useToast();

  const [expenses, setExpenses] = useState<any[]>([]);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form State
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [expenseDate, setExpenseDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [odometerReading, setOdometerReading] = useState('');
  const [fuelVolumeLitres, setFuelVolumeLitres] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');

  useEffect(() => {
    fetchExpenseData();
  }, []);

  const fetchExpenseData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [expensesRes, reportRes] = await Promise.all([
        api.getExpenses(),
        api.getTaxReport()
      ]);
      setExpenses(expensesRes.data || []);
      setReport(reportRes.data || null);
    } catch (err: any) {
      console.error('Failed to load expense data:', err);
      setError('An error occurred loading expense records.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !category || !expenseDate) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await api.createExpense({
        amount: parseFloat(amount),
        category,
        description,
        expenseDate: new Date(expenseDate).toISOString(),
        odometerReading: odometerReading ? parseInt(odometerReading) : null,
        fuelVolumeLitres: fuelVolumeLitres ? parseFloat(fuelVolumeLitres) : null,
        vehicleRegistration: vehicleRegistration || null
      });
      setSuccess('Expense transaction added successfully.');
      setAmount('');
      setCategory('');
      setDescription('');
      setOdometerReading('');
      setFuelVolumeLitres('');
      setVehicleRegistration('');
      
      // Refresh list & report stats
      await fetchExpenseData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to create expense:', err);
      setError(err.response?.data?.message || 'Failed to record expense.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete Expense Record',
      message: 'Are you sure you want to delete this expense record?',
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel',
      severity: 'danger'
    });
    if (!isConfirmed) return;

    try {
      setError(null);
      await api.deleteExpense(id);
      showToast('Expense deleted successfully.', 'success');
      await fetchExpenseData();
    } catch (err: any) {
      console.error('Failed to delete expense:', err);
      showToast(err.response?.data?.message || 'Failed to delete expense record.', 'error');
    }
  };

  const calculateFuelEfficiency = () => {
    const fuelLogs = expenses
      .filter((e: any) => e.category === 'Fuel' && e.odometerReading && e.fuelVolumeLitres)
      .sort((a: any, b: any) => new Date(a.expenseDate).getTime() - new Date(b.expenseDate).getTime());

    if (fuelLogs.length < 2) return null;

    let totalMiles = 0;
    let totalLitres = 0;

    for (let i = 1; i < fuelLogs.length; i++) {
      const miles = fuelLogs[i].odometerReading - fuelLogs[i - 1].odometerReading;
      if (miles > 0) {
        totalMiles += miles;
        totalLitres += fuelLogs[i].fuelVolumeLitres;
      }
    }

    if (totalLitres === 0) return null;

    const milesPerLitre = totalMiles / totalLitres;
    const mpg = milesPerLitre * 4.54609; // UK MPG

    return {
      milesPerLitre: milesPerLitre.toFixed(2),
      mpg: mpg.toFixed(1)
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#1a237e' }}>
        Business Expense & Tax Estimator
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

      {/* KPI Cards */}
      {report && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(46, 125, 50, 0.08)', color: 'success.main', display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>Gross Income</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>£{report.grossRevenue.toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(211, 47, 47, 0.08)', color: 'error.main', display: 'flex', alignItems: 'center' }}>
                  <ExpenseIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>Total Expenses</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>£{report.totalExpenses.toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                  <WalletIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>Net Profit</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>£{report.netProfit.toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #e0e0e0', bgcolor: report.estimatedTax > 0 ? 'rgba(237, 108, 2, 0.01)' : 'rgba(46, 125, 50, 0.02)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: report.estimatedTax > 0 ? 'rgba(237, 108, 2, 0.08)' : 'rgba(46, 125, 50, 0.12)', color: report.estimatedTax > 0 ? 'warning.main' : 'success.main', display: 'flex', alignItems: 'center' }}>
                  <TaxIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>Est. UK Tax (Optimised)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: report.estimatedTax > 0 ? '#ed6c02' : '#2e7d32' }}>
                    £{Math.min(report.estimatedTax, report.hmrcMethodEstimatedTax ?? report.estimatedTax).toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={4}>
        {/* Left Column: Form & Tax Free Allowance Progress */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Tax Free Personal Allowance Progress Card */}
            {report && (
              <Card sx={{ borderRadius: 3, p: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="text.primary">
                  UK Personal Allowance Progress
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Net Profit vs. £12,570 tax-free personal allowance.
                </Typography>

                <Box sx={{ width: '100%', mb: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (report.netProfit / 12570) * 100)}
                    color={report.netProfit > 12570 ? 'warning' : 'success'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    £{Math.max(0, Math.round(report.netProfit)).toLocaleString()} / £12,570
                  </Typography>
                  {report.netProfit < 12570 ? (
                    <Chip label="Tax-Free Limit" color="success" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                  ) : (
                    <Chip label="Taxable Income" color="warning" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                  )}
                </Box>
              </Card>
            )}

            {/* Add Expense Form */}
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="primary" /> Record Business Expense
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    <TextField
                      label="Amount (£)"
                      type="number"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      fullWidth
                      required
                    />

                    <FormControl fullWidth required>
                      <InputLabel id="category-select-label">Category</InputLabel>
                      <Select
                        labelId="category-select-label"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        label="Category"
                      >
                        {EXPENSE_CATEGORIES.map((cat) => (
                          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Date"
                      type="date"
                      value={expenseDate}
                      onChange={(e) => setExpenseDate(e.target.value)}
                      fullWidth
                      required
                    />

                    <TextField
                      label="Description / Notes"
                      placeholder="e.g. Fuel receipt, car service details"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={2}
                      fullWidth
                    />

                    {(category === 'Fuel' || category === 'Vehicle Maintenance') && (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label="Vehicle Registration"
                            placeholder="e.g. AB12 CDE"
                            value={vehicleRegistration}
                            onChange={(e) => setVehicleRegistration(e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <TextField
                            label="Odometer Reading (mi)"
                            type="number"
                            inputProps={{ min: '0' }}
                            value={odometerReading}
                            onChange={(e) => setOdometerReading(e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        {category === 'Fuel' && (
                          <Grid size={{ xs: 6 }}>
                            <TextField
                              label="Fuel Volume (Litres)"
                              type="number"
                              inputProps={{ step: '0.01', min: '0.01' }}
                              value={fuelVolumeLitres}
                              onChange={(e) => setFuelVolumeLitres(e.target.value)}
                              fullWidth
                            />
                          </Grid>
                        )}
                      </Grid>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      fullWidth
                      sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 2 }}
                    >
                      {submitting ? 'Recording...' : 'Add Expense'}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right Column: Expense History & Category Progress Bars */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box display="flex" flexDirection="column" gap={4}>
            {/* Category Breakdown Progress List */}
            {report && report.categoryBreakdown.length > 0 && (
              <Card sx={{ borderRadius: 3, p: 3, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Expenses Breakdown by Category
                </Typography>
                <Grid container spacing={2}>
                  {report.categoryBreakdown.map((breakdown: any) => {
                    const pct = (breakdown.amount / report.totalExpenses) * 100;
                    return (
                      <Grid size={{ xs: 12, sm: 6 }} key={breakdown.category}>
                        <Box sx={{ mb: 1.5 }}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="caption" fontWeight="semibold">{breakdown.category}</Typography>
                            <Typography variant="caption" color="textSecondary" fontWeight="bold">£{breakdown.amount.toFixed(2)} ({Math.round(pct)}%)</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color="error"
                            sx={{ height: 6, borderRadius: 3, bgcolor: '#eceff1' }}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Card>
            )}

            {/* Mileage & Tax Deductions Card */}
            {report && report.totalMileage !== undefined && (
              <Card sx={{ borderRadius: 3, p: 3, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TaxIcon color="primary" /> Mileage & Tax Deductions (HMRC Method)
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Total Business Mileage:</strong> {report.totalMileage.toLocaleString()} miles
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>HMRC Mileage Allowance:</strong> £{report.hmrcDeduction.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Actual Vehicle Expenses:</strong> £{report.actualVehicleExpenses.toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    {(() => {
                      const savings = report.actualVehicleExpenses - report.hmrcDeduction;
                      const fuelEfficiency = calculateFuelEfficiency();
                      
                      return (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: savings < 0 ? 'rgba(46, 125, 50, 0.04)' : 'rgba(211, 47, 47, 0.04)' }}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold' }}>
                              METHOD OPTIMIZATION
                            </Typography>
                            {savings < 0 ? (
                              <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                HMRC Standard Mileage method is more tax-efficient! (Save £{Math.abs(savings).toFixed(2)} in deductions)
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Actual expense tracking is more tax-efficient than standard mileage by £{savings.toFixed(2)}.
                              </Typography>
                            )}
                          </Box>

                          {fuelEfficiency && (
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold' }}>
                                FUEL EFFICIENCY HISTORY
                              </Typography>
                              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                Avg. Efficiency: {fuelEfficiency.mpg} MPG ({fuelEfficiency.milesPerLitre} miles/litre)
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              </Card>
            )}

            {/* Expenses List Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No expense records found. Use the form to record your business costs.
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses.map((expense) => (
                      <TableRow key={expense.id} hover>
                        <TableCell>{format(new Date(expense.expenseDate), 'dd.MM.yyyy')}</TableCell>
                        <TableCell>
                          <Chip label={expense.category} size="small" variant="outlined" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, wordBreak: 'break-all' }}>
                          <Box>
                            <Typography variant="body2">{expense.description || '-'}</Typography>
                            {(expense.odometerReading || expense.vehicleRegistration || expense.fuelVolumeLitres) && (
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {expense.vehicleRegistration && (
                                  <Chip label={expense.vehicleRegistration} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#eceff1' }} />
                                )}
                                {expense.odometerReading && (
                                  <Chip label={`${expense.odometerReading.toLocaleString()} mi`} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#e8f5e9' }} />
                                )}
                                {expense.fuelVolumeLitres && (
                                  <Chip label={`${expense.fuelVolumeLitres} L`} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#e3f2fd' }} />
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>-£{expense.amount.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={() => handleDelete(expense.id)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExpenseTracker;
